// support-page.tsx

import React, { useState } from "react";
import { useContext } from "react";
import {
  Typography,
  Card,
  CardActionArea,
  CardContent,
  TextField,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Box,
} from "@mui/material";
import { useHistory } from "react-router-dom";
import { useSupportDataLoader } from "../contexts/support-data-loader";
import Page from "../components/page";
import { getCustomLabel } from "../utils/custom-labels";
import { UserContext } from "../components/app-routes";

const SupportPage: React.FC = () => {
  const context = useContext(UserContext);
  const grower = context.grower;
  const history = useHistory();
  const [searchTerm, setSearchTerm] = useState("");
  const [openEmailModal, setOpenEmailModal] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [success, setSuccess] = useState(false);

  const formValid =
    userEmail.trim().length > 0 &&
    subject.trim().length > 0 &&
    message.trim().length > 0;

  const handleEmailOpen = () => setOpenEmailModal(true);
  const handleEmailClose = () => setOpenEmailModal(false);

  const sendEmail = async () => {
    setSubmitted(true);
    try {
      const apiBaseURL =
        window.location.hostname === "localhost"
          ? process.env.REACT_APP_API_BASE_URL || "http://localhost:8080"
          : window.location.origin;

      const response = await fetch(apiBaseURL + "/api/support-email", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: userEmail,
          subject: subject,
          message: message,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to send email");
      }

      setSuccess(true);

      // Clear fields after success
      setUserEmail("");
      setSubject("");
      setMessage("");
    } catch (error) {
      alert("There was a problem sending your email.");
    }
  };

  const { supportData } = useSupportDataLoader();

  if (!supportData || supportData.length === 0) {
    return (
      <Page title={getCustomLabel(grower ?? null, "support")}>
        <Typography>Loading support content...</Typography>
      </Page>
    );
  }

  const slugify = (str: string | undefined | null) =>
    str ? str.toLowerCase().replace(/\s+/g, "-") : "";

  const highlightMatch = (text: string, term: string): React.ReactNode => {
    if (!term) return text;

    const parts = text.split(new RegExp(`(${term})`, "gi"));
    return parts.map((part, i) =>
      part.toLowerCase() === term.toLowerCase() ? (
        <mark key={i} style={{ backgroundColor: "yellow", padding: 0 }}>
          {part}
        </mark>
      ) : (
        part
      ),
    );
  };

  const filteredResults = supportData
    .map((category) => {
      const matchingArticles = category.articles.filter(
        (article) =>
          article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          article.content.toLowerCase().includes(searchTerm.toLowerCase()),
      );

      if (
        category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        matchingArticles.length > 0
      ) {
        return {
          ...category,
          articles: matchingArticles,
        };
      }

      return null;
    })
    .filter(
      (category): category is (typeof supportData)[number] => category !== null,
    );

  return (
    <Page title={getCustomLabel(grower ?? null, "support")}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Button variant="outlined" onClick={handleEmailOpen}>
          Can't find what you need? Click here to email Us!
        </Button>
      </Box>
      <Typography variant="body1" paragraph>
        Select a category to find answers to your questions.
      </Typography>
      <TextField
        fullWidth
        variant="outlined"
        size="small"
        label="Search"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ mb: 3 }}
      />
      {filteredResults.map((category) => (
        <Box key={category.slug} mb={3}>
          <Typography variant="h6" color="primary" gutterBottom>
            {highlightMatch(category.name, searchTerm)}
          </Typography>

          {category.articles.map((article) => (
            <Box key={article.slug} mb={1}>
              <Card>
                <CardActionArea
                  onClick={() =>
                    history.push(
                      `/home/support/${slugify(category.name)}/${article.slug}`,
                    )
                  }
                >
                  <CardContent>
                    <Typography variant="subtitle1">
                      {highlightMatch(article.title, searchTerm)}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Box>
          ))}
        </Box>
      ))}
      {filteredResults.length === 0 && (
        <Typography>No matching articles or categories found.</Typography>
      )}
      <Dialog
        open={openEmailModal}
        onClose={handleEmailClose}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Email Sent</DialogTitle>
        <DialogContent dividers>
          {success ? (
            <Typography variant="body1" color="primary">
              Your message has been sent! We’ll get back to you soon.
            </Typography>
          ) : (
            <>
              <Typography variant="body1" gutterBottom>
                If you can’t find what you’re looking for, feel free to email us
                with your question.
              </Typography>
              <TextField
                autoFocus
                margin="dense"
                label="Your Email"
                type="email"
                fullWidth
                variant="outlined"
                value={userEmail}
                onChange={(e) => setUserEmail(e.target.value)}
                error={submitted && userEmail.trim().length === 0}
                helperText={
                  submitted && userEmail.trim().length === 0
                    ? "Email is required"
                    : ""
                }
              />
              <TextField
                margin="dense"
                label="Subject"
                type="text"
                fullWidth
                variant="outlined"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                error={submitted && subject.trim().length === 0}
                helperText={
                  submitted && subject.trim().length === 0
                    ? "Subject is required"
                    : ""
                }
              />
              <TextField
                margin="dense"
                label="Your Question"
                type="text"
                multiline
                rows={4}
                fullWidth
                variant="outlined"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                error={submitted && message.trim().length === 0}
                helperText={
                  submitted && message.trim().length === 0
                    ? "Message is required"
                    : ""
                }
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          {success ? (
            <Button
              onClick={() => {
                setSuccess(false);
                handleEmailClose();
              }}
              variant="contained"
            >
              Close
            </Button>
          ) : (
            <>
              <Button onClick={handleEmailClose}>Cancel</Button>
              <Button
                onClick={sendEmail}
                variant="contained"
                disabled={!formValid}
              >
                Send
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Page>
  );
};

export default SupportPage;
